import React from 'react';
import { useFieldArray, useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import { ConditionWarningDialog } from './question-list/ConditionWarningDialog';
import { DraggableQuestionList } from './question-list/DraggableQuestionList';
import { QuestionItem } from './question-list/QuestionItem';
import { useQuestionOperations } from '@/features/questionnaires/hooks/questionnaire/useQuestionOperations';
import { QuestionOperationsProvider } from '@/features/questionnaires/contexts/QuestionOperationsProvider';
import type { QuestionnaireFormData } from '@/features/questionnaires/types/questionnaire.interface';

export const QuestionsSection: React.FC = () => {
  const { t } = useTranslation();
  const {
    control,
    getValues,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<QuestionnaireFormData>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'questions',
    keyName: '_id',
  });

  const questions = useWatch({ control, name: 'questions' });

  const operations = useQuestionOperations({
    questions,
    getValues,
    setValue,
    move,
    remove,
    append,
  });

  return (
    <QuestionOperationsProvider
      value={{
        onTypeChange: operations.handleTypeChange,
        onOptionsChange: operations.handleOptionsChange,
        onAllowMultipleChange: operations.handleAllowMultipleChange,
        onRemove: operations.handleDelete,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <DraggableQuestionList
          fields={fields}
          onDragEnd={operations.handleReorder}
        >
          {fields.map((field, index) => (
            <QuestionItem
              key={field._id}
              field={field}
              index={index}
              questions={questions ?? []}
            />
          ))}
        </DraggableQuestionList>

        <Button
          variant="outlined"
          onClick={operations.addNewQuestion}
          disabled={isSubmitting}
          fullWidth
        >
          {t('buttons.addQuestion')}
        </Button>

        <ConditionWarningDialog
          open={operations.warningState.open}
          onConfirm={operations.warningState.onConfirm}
          brokenConditions={operations.warningState.brokenConditions}
          questions={questions || []}
          warningType={operations.warningState.type}
        />
      </Box>
    </QuestionOperationsProvider>
  );
};
